import Shops from '../sidechain/handles/shopsHandle';
import Products from '../sidechain/handles/productHandle';
import ProductReviews from '../sidechain/handles/productReviewHandle';
import { getPrivateKey, getCurrentUser, updateUserRole } from './user.service';
import { status_response, role } from './constants';
import { handleSearchElasticProduct } from '../elasticsearch/elasticProduct';
import { convertToFloat } from '../utils/commonUtil';
import { elasticShopOverview } from '../elasticsearch/elasticShop';
import { searchProductsByIdsES } from './product.service';
import { escapeString } from '../utils/commonUtil';

/**
 * Register new shop
 * @param name
 * @param zipcode
 * @param prefecture
 * @param address
 * @param phoneNumber
 * @param ethernetAddress
 * @return {status, messageId} status is 1 if successed, 0 if failed
 */
async function register(name, zipcode, prefecture, address, phoneNumber, ethernetAddress) {
  if (
    typeof name !== 'string' ||
    typeof zipcode !== 'string' ||
    typeof prefecture !== 'string' ||
    typeof address !== 'string' ||
    typeof phoneNumber !== 'string' ||
    typeof ethernetAddress !== 'string'
  ) {
    return {
      status: status_response.error,
      result: {
        message: 'Invalid param',
        messageId: 'EA2003_Content'
      }
    };
  }
  try {
    const currentUser = await getCurrentUser();
    const privateKey = await getPrivateKey(currentUser);
    const imagesPath = ''; // firebase images path null when register shop
    const shopHandler = new Shops(privateKey);

    // Check if shop is already registered!
    const isShopRegistered = await shopHandler.checkIsRegistered();
    if (!isShopRegistered) {
      // Register new shop
      let result = await shopHandler.registerShop(
        name,
        ethernetAddress,
        imagesPath,
        JSON.stringify({
          zipcode: zipcode,
          prefecture: prefecture,
          address: address,
          phoneNumber: phoneNumber
        })
      );
      if (!result || !result.cryptoAddress)
        return {
          status: status_response.error,
          result: {
            message: 'Register shop failed',
            messageId: 'EF2143_Content'
          }
        };
    } else {
      return {
        status: status_response.error,
        result: {
          message: 'Register failue. Shop is already registed.',
          messageId: 'EF2143_Content'
        }
      };
    }
    // Update role of user to true
    const updateRole = await updateUserRole(currentUser.uid, role.SHOP);

    if (updateRole) {
      // The process is successful
      return {
        status: status_response.success,
        result: {
          data: true,
          message: 'Register Successful!'
        }
      };
    }

    // Otherwise return false
    return {
      status: status_response.error,
      result: {
        message: 'Update shop role failed',
        messageId: 'EA2002_Content'
      }
    };
  } catch (error) {
    console.log(error);
    return {
      status: status_response.error,
      result: {
        message: error,
        messageId: 'EF2016_Content'
      }
    };
  }
}

/**
 * check shop is registered
 * @param user if null, will fetch the current user from firebase and get private key
 * @param privateKey
 * @returns status success if shop is registered; error otherwise
 */
async function checkShopIsRegistered(user, privateKey) {
  try {
    if (!privateKey || privateKey.length === 0) {
      privateKey = await getPrivateKey(user);
    }

    const shopHandle = new Shops(privateKey);
    let result = await shopHandle.checkIsRegistered();
    if (result) {
      return {
        status: status_response.success,
        result: {
          data: result,
          messageId: 'Shop is register!'
        }
      };
    } else {
      return {
        status: status_response.not_register,
        result: {
          message: 'Shop is not registered!',
          messageId: 'EF2007_Content'
        }
      };
    }
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
}

async function getShopInfo(pubKeySideChain, privateKey) {
  try {
    if (!privateKey || privateKey.length === 0) {
      try {
        privateKey = await getPrivateKey();
      } catch (err) {
        // No Private key for current session.
      }
    }

    const shopHandle = new Shops(privateKey);
    const rawShopInfo = await shopHandle.getShopInfo(pubKeySideChain);

    /* rawShopInfo example
    {
      "name": "phuong shop",
      "etherAddr": "0x5A2fa177d374C43C58Becf07abd05022f5206bf4",
      "image": "[\\"https://firebasestorage.googleapis.com/v0/b/ecblockchain.appspot.com/o/oNtwhC2OnbXtpLdaLzeNunBRl3o2%2Fshop%2F8b7e6117-52a1-a05a-1efd-77a5b0ee046f42748561_169679050605964_2038546389562032128_n.jpeg?alt=media&token=97822406-016a-48eb-9214-709bde5fab31\\"]",
      "description": "{\\"zipcode\\":\\"1231231\\",\\"prefecture\\":\\"青森県\\",\\"address\\":\\"\\",\\"phoneNumber\\":\\"1231231231\\",\\"introduction\\":\\"Nothing\\",\\"businessTime\\":\\"＊3MB以内の画像ファイル(gif, jpg, png)を選択してください。（推奨サイズ：360×360px\\"}",
      "totalPointReview": 0,
      "numOfReview": 0,
      "createdAt": 1544422593,
"updatedAt": 1544438861
    }*/

    let image = null;
    let description = null;
    if (rawShopInfo.image) {
      try {
        image = JSON.parse(rawShopInfo.image);
      } catch (e) {
        /* ignored */
      }
    }
    if (rawShopInfo.description) {
      try {
        description = JSON.parse(rawShopInfo.description);
      } catch (e) {
        /* ignored */
      }
    }

    const productReviewHandle = new ProductReviews(privateKey);
    const statistic = await productReviewHandle.getStatisticReviewShop(pubKeySideChain);
    const result = {
      name: rawShopInfo.name,
      etherAddr: rawShopInfo.etherAddr,
      image,
      description,
      statistic,
      createdAt: rawShopInfo.createdAt,
      updatedAt: rawShopInfo.updatedAt
    };

    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'Get shop infor success!'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EA2009_Content'
      }
    };
  }
}

async function getMyShopInfo(privateKey) {
  try {
    if (!privateKey || privateKey.length === 0) {
      privateKey = await getPrivateKey();
    }
    const shopHandle = new Shops(privateKey);
    let result = await shopHandle.getMyShopInfo();
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'Get shop infor success!'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
}

// get product list of shop address
async function getProductOfDiffShop({ publicAddr, page, limit, orderBy, keyword }) {
  try {
    let privateKey;
    try {
      privateKey = await getPrivateKey();
    } catch (err) {
      // No Private key for current session.
    }
    const shopHandle = new Shops(privateKey);
    let listProductID = await shopHandle.getProductOfDiffShop(publicAddr);
    return getProductsOfShopWithParams(
      {
        page,
        limit,
        name: keyword,
        status: 1,
        orderBy
      },
      privateKey,
      listProductID
    );
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
}

// function get list product or search product in shop
// search by name and status of product param: status: 1-Publish 2-Un-publish, name: name exactly of product
// get products in shop page start 1, limit: length products need get
async function getProductsOfShopWithParams(
  { page: offset, limit, name, status, orderBy },
  private_key = undefined,
  listProductIds = []
) {
  try {
    if (!listProductIds || listProductIds.length === 0) {
      let privateKey;
      if (!private_key) {
        privateKey = await getPrivateKey();
      } else {
        privateKey = private_key;
      }
      const shopHandle = new Shops(privateKey);
      listProductIds = await shopHandle.getProductIdsOfShop();
    }

    let result = {};
    if (listProductIds.length > 0) {
      result = await searchProductsByIdsES({
        ids: listProductIds,
        keyword: name,
        status,
        offset: offset > 0 ? (offset - 1) * limit : 0,
        range: limit,
        orderBy,
        fieldsSearch: ['name']
      });
    }

    if (result.status && result.result && result.result.total >= 0) {
      return {
        status: result.result.total === 0 ? status_response.error : status_response.success,
        result: {
          data: result.result.data,
          total: result.result.total,
          totalProductIds: listProductIds,
          messageId: result.result.total === 0 ? 'EA2004_Content' : 'Get product list success!'
        }
      };
    }

    return {
      status: status_response.error,
      result: {
        data: [],
        total: 0,
        totalProductIds: listProductIds,
        messageId: 'EA2004_Content'
      }
    };
  } catch (error) {
    console.log(error);
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EA2004_Content'
      }
    };
  }
}

/**
 *
 * @param response
 * @param total
 */
const resultToClientElasticSearch = async function(response, total) {
  let result = [];
  if (response.error && !response.status) {
    return {
      status: status_response.error,
      error: response.error,
      total: total
    };
  } else {
    if (response.result && response.result.length >= 0) {
      result = await response.result.map(product => {
        product = product._source;
        return product;
      });
    }
    return {
      status: status_response.success,
      result: {
        data: result,
        messageId: 'get list product successfully!',
        total: total
      }
    };
  }
};

/**
 *
 * @param {*} param
 *      publicAddr: publicAddress sidechain
        keyword: any string
        orderBy: 1,2,3
        page:  1 -> n
        limit:
        status: 1,0
 */
async function searchElasticProductsWithShop({ publicAddr, keyword, orderBy, page, limit, status }) {
  let must = [];
  const sort = [];

  const from = (page - 1) * limit;

  must.push({ match: { owner: publicAddr } });

  switch (orderBy) {
    case 1:
      sort.push({
        'productDetail.createdAt': { order: 'asc', unmapped_type: 'long' }
      });
      break;
    case 2:
      sort.push({ price: { order: 'asc', unmapped_type: 'double' } });
      break;
    case 3:
      sort.push({ price: { order: 'desc', unmapped_type: 'double' } });
      break;
    default:
      return {
        status: 0,
        messageId: 'EA2023_Content',
        message: 'Invalid parameter!'
      };
  }

  if (keyword && keyword.trim() && keyword.length > 0) {
    must.push({
      multi_match: {
        query: escapeString(keyword.trim()),
        fields: ['shopName', 'name']
      }
    });
  }

  if (status > -1) {
    must.push({ match: { status: status } });
  }

  try {
    let result = await handleSearchElasticProduct(
      {
        bool: {
          must
        }
      },
      from,
      limit,
      sort
    );
    // console.log('result elastic: ', result)
    if (result.length === 0) {
      return {
        status: 1,
        result: {
          data: [],
          messageId: 'get list product successfully!',
          total: 0
        }
      };
    }
    return resultToClientElasticSearch(result, result.total);
  } catch (error) {
    return getProductsOfShopWithParams({
      page: page,
      limit: limit,
      name: keyword,
      status: status
    });
  }
}

async function searchProductsByParamsOfDiffShop(publicAddr, keyword, orderBy, page, limit) {
  if (
    (publicAddr && typeof publicAddr !== 'string') ||
    (keyword && typeof keyword !== 'string') ||
    (orderBy && typeof orderBy !== 'number') ||
    typeof page !== 'number' ||
    typeof limit !== 'number'
  ) {
    return { status: 0, message: 'Invalid parameter!' };
  }

  try {
    let total;
    let listProducts = [];
    const privateKey = await getPrivateKey();
    const productHandle = new Products(privateKey);
    const shopHandle = new Shops(privateKey);

    let listProductID = await shopHandle.getProductOfDiffShop(publicAddr);
    let indexArr = new Array(20).fill(1);

    await Promise.all(
      indexArr.map((val, i) => {
        return productHandle.getAllInfoById(listProductID[i]);
      })
    ).then(result => {
      listProducts = result;
    });
    // search by name
    if (keyword && keyword.trim() !== '') {
      listProducts = listProducts.filter(product => {
        return product[0].name.toLowerCase().indexOf(keyword.trim().toLowerCase()) > -1;
      });
    }

    total = listProducts.length;

    let result = [];
    let begin = (page - 1) * limit;
    let end = begin + limit;
    if (end >= listProducts.length) {
      end = listProducts.length;
    }

    for (let i = begin; i < end; i++) {
      result.push(listProducts[i]);
    }

    result.sort((a, b) => {
      switch (orderBy) {
        case 1:
          return 0;
        case 2:
          return parseInt(a.price) - parseInt(b.price);
        case 3:
          return parseInt(b.price) - parseInt(a.price);
        default:
          return 0;
      }
    });
    return {
      status: 1,
      result: {
        data: result,
        messageId: 'Get products of shop Successful!',
        total: parseInt(total, 10)
      }
    };
  } catch (error) {
    return {
      status: 0,
      error: error,
      message: 'Get list product with status of shop failure!'
    };
  }
}

async function updateShop(shop, privateKey) {
  let name, etherAddress, image, description;
  name = shop.name;
  etherAddress = shop.etherAddress;
  image = shop.image;
  description = shop.description;
  if (
    typeof name !== 'string' ||
    typeof etherAddress !== 'string' ||
    typeof image !== 'string' ||
    typeof description !== 'string'
  ) {
    return {
      status: status_response.error,
      result: {
        message: 'Invalid param',
        messageId: 'EA2025_Content'
      }
    };
  }
  try {
    if (!privateKey || privateKey.length === 0) {
      privateKey = await getPrivateKey();
    }
    const shopHandle = new Shops(privateKey);
    let response = await shopHandle.updateShop(name, etherAddress, image, description);
    return {
      status: status_response.success,
      result: {
        data: response,
        messageId: 'EF2126_Content'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId || 'EF2143_Content'
      }
    };
  }
}

async function getStatisticReview(publicAddr) {
  try {
    let privateKey;
    try {
      privateKey = await getPrivateKey();
    } catch (err) {
      // No Private key for current session.
    }
    const shopHandle = new Shops(privateKey);
    const response = await shopHandle.getStatisticReview(publicAddr);
    return {
      status: status_response.success,
      result: {
        data: response,
        messageId: 'Get static review success!'
      }
    };
  } catch (error) {
    console.log(error);
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: 'EA2009_Content'
      }
    };
  }
}

/**
 * Get overview in shop
 * @return data overview
 * example data return:
 * {numOfProductPublic: 15, numOfProductUnpublic: 2, revenueToday: 1111111126482946.8, revenueCurrentMonth: 1111111126482947.6, revenueLastMonth: 0}
 */
async function getOverviewShop() {
  try {
    const data = await elasticShopOverview();
    return {
      status: status_response.success,
      result: {
        data: {
          numOfProductPublic: data.numOfProductPublic,
          numOfProductUnpublic: data.numOfProductUnpublic,
          revenueToday: convertToFloat(data.revenueToday),
          revenueCurrentMonth: convertToFloat(data.revenueCurrentMonth),
          revenueLastMonth: convertToFloat(data.revenueLastMonth),
          hasOrderPaymented: data.hasOrderPaymented
        },
        messageId: 'Get overview success!'
      }
    };
  } catch (error) {
    return {
      status: status_response.error,
      result: {
        message: error.message,
        messageId: error.messageId
      }
    };
  }
}

export {
  getProductOfDiffShop,
  register,
  checkShopIsRegistered,
  getProductsOfShopWithParams,
  getShopInfo,
  getMyShopInfo,
  updateShop,
  getStatisticReview,
  searchProductsByParamsOfDiffShop,
  searchElasticProductsWithShop,
  getOverviewShop
};
